import React from 'react';
import classnames from 'classnames';

import Button, { BUTTON_TYPES } from '@buyside/components/presentation/Button/button';
import FilterChecklist from '@buyside/components/presentation/Filters/FilterChecklist/filterChecklist';
import FilterSliderRange from '@buyside/components/presentation/Filters/FilterSliderRange/FilterSliderRange';
import SelectableDropdown from '@buyside/components/presentation/SelectableDropdown/selectableDropdown';

import { generateRangeMapping } from '@buyside/util/filterSliderUtil/filterSliderUtil';
import formatNumber from '@buyside/util/formatNumber';

import { sliders } from '@buyside/util/ideaFilterConstants';
import { labelizedFilterListOptions } from '@buyside/components/presentation/Filters/filterUtil';

const { certifications, fundTypes, graduateDegrees, undergradDegrees } = labelizedFilterListOptions;

const {
  act: { increments: actIncrements, ticks: actTicks },
  gpa: { increments: gpaIncrements, ticks: gpaTicks },
  sat: { increments: satIncrements, ticks: satTicks },
  years_of_experience: { increments: yearsExpIncrements, ticks: yearsExpTicks }
} = sliders;

import styles from './jobApplicationIndexPageFilterStyles.scss';

const JobApplicationIndexPageFilter: React.FunctionComponent = (): React.ReactElement => {
  const [showMoreFilters, toggleMoreFilters] = React.useState(false);

  return (
    <div className={styles.container}>
      <div
        className={classnames(styles.filterContainer, {
          [styles.showFilter]: showMoreFilters
        })}
      >
        <SelectableDropdown title="ACT Score" analyticsTag="applicant_act_scores">
          <div className={styles.rangeSlider}>
            <FilterSliderRange
              increments={actIncrements}
              paramName="act"
              mapping={generateRangeMapping(actIncrements, actTicks, formatNumber)}
              handleTipFormatter={formatNumber}
            />
          </div>
        </SelectableDropdown>

        <SelectableDropdown title="Certifications" analyticsTag="certifications">
          <FilterChecklist paramName="certifications[]" filterList={certifications} />
        </SelectableDropdown>

        <SelectableDropdown
          title="Educational Degrees"
          analyticsTag="applicant_educational_degrees"
        >
          <FilterChecklist
            title="Undergraduate"
            paramName="degrees[]"
            filterList={undergradDegrees}
          />
          <FilterChecklist title="Graduate" paramName="degrees[]" filterList={graduateDegrees} />
        </SelectableDropdown>

        <SelectableDropdown title="Firm Type" analyticsTag="firm_type">
          <FilterChecklist paramName="firm_type[]" filterList={fundTypes} />
        </SelectableDropdown>

        <SelectableDropdown title="GPA" analyticsTag="applicant_gpa">
          <div className={styles.rangeSlider}>
            <FilterSliderRange
              paramName="gpa"
              mapping={generateRangeMapping(gpaIncrements, gpaTicks, formatNumber)}
              increments={gpaIncrements}
              handleTipFormatter={formatNumber}
            />
          </div>
        </SelectableDropdown>

        <SelectableDropdown title="SAT Score" analyticsTag="applicant_sat_scores">
          <div className={styles.rangeSlider}>
            <FilterSliderRange
              paramName="sat"
              mapping={generateRangeMapping(satIncrements, satTicks, formatNumber)}
              increments={satIncrements}
              handleTipFormatter={formatNumber}
            />
          </div>
        </SelectableDropdown>

        <SelectableDropdown title="Years Of Experience" analyticsTag="years_of_experience">
          <div className={styles.rangeSlider}>
            <FilterSliderRange
              paramName="years_of_experience"
              mapping={generateRangeMapping(yearsExpIncrements, yearsExpTicks, formatNumber)}
              increments={yearsExpIncrements}
              handleTipFormatter={formatNumber}
            />
          </div>
        </SelectableDropdown>
      </div>

      <Button
        onClick={(): void => {
          toggleMoreFilters(!showMoreFilters);
        }}
        className={classnames(styles.toggleFilterContainer, {
          [styles.moreFiltersShowing]: showMoreFilters,
          [styles.lessFiltersShowing]: !showMoreFilters
        })}
        type={BUTTON_TYPES.LINK}
        text={showMoreFilters ? 'Less Filters' : 'More Filters'}
      />
    </div>
  );
};

export default JobApplicationIndexPageFilter;
